import React from 'react';
import { getStatusDisplayName, getStatusColor, formatDateTime } from '../../utils/helpers';
import { mapStatusToCategory } from '../../utils/statusCategoryMapper';
import LoadingSpinner from '../common/LoadingSpinner';

const DashboardStats = ({ stats, recentContainers, loading, onViewAllContainers }) => {
  const [selectedStatus, setSelectedStatus] = React.useState(null);

  if (loading) {
    return <LoadingSpinner text="Loading dashboard stats..." />;
  }

  // Define icons and labels for each status
  const STATUS_META = {
    BOOKED: { icon: '📋', label: 'Booked' },
    GATED_IN: { icon: '🏭', label: 'Gate In' },
    LOADED: { icon: '🚢', label: 'Loaded' },
    IN_TRANSIT: { icon: '🌊', label: 'In Transit' },
    DISCHARGED: { icon: '🏗️', label: 'Discharged' },
    READY_FOR_PICKUP: { icon: '📦', label: 'Ready for Pickup' },
    DELIVERED: { icon: '✅', label: 'Delivered' },
    COMPLETED: { icon: '✔️', label: 'Completed' },
    SHIPPER_PICK_UP: { icon: '📦', label: 'Shipper Pick Up' }
  };

  // Get unique internal status codes from STATUS_CATEGORY_MAP
  const uniqueStatusCodes = Array.from(new Set(Object.values(require('../../utils/statusCategoryMapper').STATUS_CATEGORY_MAP)));

  const statusCards = uniqueStatusCodes.map(status => ({
    status,
    count: stats?.[status] || 0,
    icon: STATUS_META[status]?.icon || '❓',
    label: STATUS_META[status]?.label || status
  }));

  // Normalize status for filtering
  const normalizeStatus = (status) => mapStatusToCategory(status);

  const filteredContainers = selectedStatus
  ? recentContainers.filter(c => normalizeStatus(c.status) === selectedStatus)
  : recentContainers;

  // Heading logic
  const heading = selectedStatus
    ? `${getStatusDisplayName(selectedStatus)} Containers`
    : 'Recent Containers';

  return (
    <div className="dashboard-stats">
      {/* Stats Grid */}
      <div className="stats-grid">
        {statusCards.map(({ status, count, icon }) => (
          <div
            key={status}
            className={`stat-card${selectedStatus === status ? ' selected' : ''}`}
            onClick={() => {
              if (selectedStatus !== status) setSelectedStatus(status);
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-content">
              <div className="stat-icon">{icon}</div>
              <div className="stat-numbers">
                <div className="stat-number">{count}</div>
                <div className="stat-label">{statusCards.find(card => card.status === status)?.label || getStatusDisplayName(status)}</div>
              </div>
            </div>
            <div className={`status-indicator ${getStatusColor(status)}`}></div>
          </div>
        ))}
      </div>

      {/* Recent Containers Preview */}
      <div className="recent-containers-preview">
        <div className="section-header">
          <h3>{heading}</h3>
          <button 
            onClick={() => {
              setSelectedStatus(null);
              if (onViewAllContainers) onViewAllContainers();
            }}
            className="btn btn-sm btn-primary"
          >
            View All Containers
          </button>
        </div>
        {filteredContainers.length > 0 ? (
          <div className="preview-grid">
            {filteredContainers.slice(0, 6).map(container => (
              <div key={container.id} className="preview-card">
                <div className="preview-header">
                  <div className="container-number">{container.containerNumber}</div>
                  <span className={`status-badge small ${container.status}`}>
                    {getStatusDisplayName(mapStatusToCategory(container.status))}
                  </span>
                </div>
                <div className="preview-booking">Booking: {container.bookingNumber}</div>
                <div className="preview-vessel">{container.vesselName || 'No vessel assigned'}</div>
                <div className="preview-route">
                  {container.portOfLoading} → {container.portOfDischarge}
                </div>
                <div className="preview-updated">
                  Updated: {formatDateTime(container.updatedAt)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No containers found</p>
          </div>
        )}
      </div>

      <style>{`
        .dashboard-stats {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.5rem;
        }
        
        .stat-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          position: relative;
          overflow: hidden;
          transition: transform 0.3s, box-shadow 0.3s;
        }
        
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }
        
        .stat-content {
          display: flex;
          align-items: center;
          gap: 1rem;
          position: relative;
          z-index: 2;
        }
        
        .stat-icon {
          font-size: 2.5rem;
          opacity: 0.8;
        }
        
        .stat-numbers {
          flex: 1;
        }
        
        .stat-number {
          font-size: 2.2rem;
          font-weight: 800;
          color: #333;
          margin-bottom: 0.25rem;
          line-height: 1;
        }
        
        .stat-label {
          color: #666;
          font-size: 0.9rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .status-indicator {
          position: absolute;
          top: 0;
          right: 0;
          width: 6px;
          height: 100%;
        }
        
        .status-indicator.blue { background: linear-gradient(135deg, #1976d2, #42a5f5); }
        .status-indicator.green { background: linear-gradient(135deg, #2e7d32, #4caf50); }
        .status-indicator.orange { background: linear-gradient(135deg, #f57c00, #ff9800); }
        .status-indicator.purple { background: linear-gradient(135deg, #7b1fa2, #9c27b0); }
        .status-indicator.teal { background: linear-gradient(135deg, #00796b, #009688); }
        .status-indicator.gray { background: linear-gradient(135deg, #616161, #9e9e9e); }
        
        .recent-containers-preview {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #f8f9fa;
        }
        
        .section-header h3 {
          margin: 0;
          color: #333;
          font-size: 1.4rem;
        }
        
        .preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.25rem;
        }
        
        .preview-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 10px;
          padding: 1.25rem;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .preview-card:hover {
          background: white;
          border-color: #007bff;
          box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
          transform: translateY(-2px);
        }
        
        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 0.75rem;
        }
        
        .container-number {
          font-size: 1.1rem;
          font-weight: 700;
          color: #007bff;
          font-family: 'Courier New', monospace;
        }
        
        .status-badge.small {
          padding: 0.3rem 0.6rem;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .preview-booking {
          color: #666;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }
        
        .preview-vessel {
          color: #333;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        .preview-route {
          color: #007bff;
          font-size: 0.85rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        .preview-updated {
          color: #888;
          font-size: 0.8rem;
          font-style: italic;
        }
        
        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #666;
          grid-column: 1 / -1;
        }
        
        .quick-actions-section {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 2rem;
        }
        
        .quick-actions-section h3 {
          margin: 0 0 1.5rem 0;
          color: #333;
          font-size: 1.4rem;
          text-align: center;
        }
        
        .quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
        }
        
        .quick-action-card {
          background: linear-gradient(135deg, #f8f9fa, #e9ecef);
          border: 2px solid transparent;
          border-radius: 12px;
          padding: 1.5rem;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }
        
        .quick-action-card:hover {
          background: linear-gradient(135deg, #007bff, #0056b3);
          border-color: #007bff;
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
        }
        
        .quick-action-card:hover .action-icon,
        .quick-action-card:hover .action-label,
        .quick-action-card:hover .action-desc {
          color: white;
        }
        
        .action-icon {
          font-size: 2.5rem;
          transition: color 0.3s;
        }
        
        .action-label {
          font-size: 1.1rem;
          font-weight: 700;
          color: #333;
          transition: color 0.3s;
        }
        
        .action-desc {
          font-size: 0.9rem;
          color: #666;
          transition: color 0.3s;
          line-height: 1.4;
        }
        
        /* Status badge colors */
        .status-badge.BOOKED {
          background-color: #e3f2fd;
          color: #1976d2;
          border: 1px solid #bbdefb;
        }
        
        .status-badge.GATED_IN {
          background-color: #e8f5e8;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        }
        
        .status-badge.LOADED {
          background-color: #fff3e0;
          color: #f57c00;
          border: 1px solid #ffe0b2;
        }
        
        .status-badge.IN_TRANSIT {
          background-color: #f3e5f5;
          color: #7b1fa2;
          border: 1px solid #e1bee7;
        }
        
        .status-badge.DISCHARGED {
          background-color: #e0f2f1;
          color: #00796b;
          border: 1px solid #b2dfdb;
        }
        
        .status-badge.READY_FOR_PICKUP {
          background-color: #e8f5e8;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        }
        
        .status-badge.DELIVERED {
          background-color: #f5f5f5;
          color: #616161;
          border: 1px solid #e0e0e0;
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 1rem;
          }
          
          .preview-grid {
            grid-template-columns: 1fr;
          }
          
          .quick-actions-grid {
            grid-template-columns: 1fr;
          }
          
          .section-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
            text-align: center;
          }
          
          .stat-card {
            padding: 1.25rem;
          }
          
          .stat-number {
            font-size: 1.8rem;
          }
          
          .recent-containers-preview,
          .quick-actions-section {
            padding: 1.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }
          
          .stat-content {
            gap: 0.75rem;
          }
          
          .stat-icon {
            font-size: 2rem;
          }
          
          .preview-card {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardStats;